import { useEffect, useRef, useState } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';

import { DBExtractTestCase } from '@core/types/types.db';
import {
  TesterExtractRunEndpoint,
  TesterExtractSaveEndpoint,
} from '@core/types/types.endpoint.tester';
import { classifyTitle } from '@core/util/util.classifyTitle';
import { Button } from '@frontend/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu';
import { Textarea } from '@frontend/components/ui/textarea';
import { useSingleSearchParam } from '@frontend/shared/hooks/useSingleSearchParam';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';

export function TesterExtractItem({
  item,
  onSave,
  index,
  refetch,
  extract,
}: {
  item: DBExtractTestCase;
  onSave: (item: DBExtractTestCase) => void;
  index: number;
  refetch: () => void;
  extract: boolean;
}) {
  const [value, setValue] = useState(item.input_text);
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useSingleSearchParam(`${index}`);
  const [isExpandable, setIsExpandable] = useState(false);

  const saveSubmitter = useSubmitter(async (classify?: 'none' | 'question') => {
    const [, res] = await callApi<TesterExtractSaveEndpoint>(
      '/tester/extract/save',
      {
        ts: item.ts,
        input_text: value,
        classify: classify ?? item.classify,
      },
    );

    if (res) {
      onSave(res);
    }
  });

  const run = useSubmitter(async (target: 'status_quo' | 'experiment') => {
    await callApi<TesterExtractRunEndpoint>('/tester/extract/run', {
      ts: [item.ts],
      target,
      skip_extract: !extract,
    });
    refetch();
  });

  const type = item.classify;
  useEffect(() => {
    // If height is greater than 120px, set isExpandable to true
    if (ref.current && ref.current.scrollHeight > 120) {
      setIsExpandable(true);
    }
  }, [value]);

  return (
    <div
      className="overflow-hidden flex"
      ref={ref}
      style={{ maxHeight: expanded ? undefined : '120px' }}
    >
      <div className="w-2/5 flex-none max-w-[720px] p-3 flex">
        <div className="mt-1 mr-2 w-8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={saveSubmitter.isSubmitting}
                variant="ghost"
                size="sm"
                className="px-0"
              >
                {classifyTitle(type).split(' ')[0]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => saveSubmitter.submit('none')}>
                {classifyTitle('none')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => saveSubmitter.submit('question')}
              >
                {classifyTitle('question')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isExpandable && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setExpanded(expanded ? undefined : '1')}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <IoIosArrowUp className="h-5 w-5" />
              ) : (
                <IoIosArrowDown className="h-5 w-5" />
              )}
            </Button>
          )}
        </div>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            if (value !== item.input_text) {
              saveSubmitter.submit();
            }
          }}
          disabled={saveSubmitter.isSubmitting}
          placeholder="Message"
          className="min-h-[42px]"
          style={{ resize: 'vertical' }}
        />
      </div>

      <div className="w-full flex justify-between items-start">
        <div className="w-1/2 p-3">
          <Button
            variant="ghost"
            size="sm"
            disabled={item.running_status_quo}
            onClick={() => run.submit('status_quo')}
          >
            {classifyTitle(item.result.status_quo_classify)}
          </Button>
          {item.result.status_quo_qna.map((qna, index) => (
            <div key={index} className="mt-3">
              <h4 className="text-xs font-semibold">{qna.question}</h4>
              <p className="text-xs">{qna.answer}</p>
            </div>
          ))}
        </div>
        <div className="w-1/2 p-3">
          <Button
            variant="ghost"
            size="sm"
            disabled={item.running_experiment}
            onClick={() => run.submit('experiment')}
          >
            {classifyTitle(item.result.experiment_classify)}
          </Button>
          {item.result.experiment_qna.map((qna, index) => (
            <div key={index} className="mt-3">
              <h4 className="text-xs font-semibold">{qna.question}</h4>
              <p className="text-xs">{qna.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
